import React, { useState, useEffect, ReactNode } from "react";
import ErrorsList from "components/Forms/ErrorsList";
import { createUseStyles } from "react-jss";
import {
  EuiFlexGroup,
  EuiSpacer,
  EuiForm,
  EuiDescriptionList,
  EuiText,
  EuiFlexItem,
} from "@elastic/eui";
import Validation from "Classes/Validation";
import FDFormControl from "./FDFormControl";
import { IControl } from "types/app";
import FdFormButtons from "./FdFormButtons";
import { Flyout } from "components/Flyout/Flyout";
import Button from "components/Buttons/Button";
import Tabs from "components/Tabs/Tabs";
import { COLORS } from "Consts";

const useStyles = createUseStyles({
  tab: {
    "&.euiTab": {
      color: COLORS.$euiColorMediumShade + "!important",
    },
    "&..euiTab-isSelected": {
      color: "var(--primary-color) !important;",
      boxShadow: "inset 0 -2px 0 #14A05A",
      borderBottomColor: "#14A05A !important",
    },
  },
});

interface FlyoutFormProps {
  cancelHandler?: () => any;
  controls?: Partial<IControl>[];
  //can we read only instead of a form
  readonly?: boolean;
  readonlyList?: { field: string; label: string }[];
  saveErrors?: { [key: string]: string };
  saveHandler?: (data: any, selectedTabId: string) => any;
  source: any;
  tabs?: Array<{
    controls: Partial<IControl>[];
    list?: Array<{
      description: NonNullable<ReactNode>;
      title: NonNullable<ReactNode>;
    }>;
    name: string;
    readonly?: boolean;
    isProfile?: boolean;
  }>;
  tabsValidationShared?: boolean;
  title?: string;
  topControlsContent?: ReactNode;
  children?: ReactNode;
  isSaving?: boolean;
  outerChanges?: boolean | undefined;
  size?: string;
}

const FlyoutForm: React.FunctionComponent<FlyoutFormProps> = ({
  controls = [],
  readonly = false,
  readonlyList: readonlyList = [], // readonly EuiDescriptionList
  source: InitialObject = {},
  title = "",
  topControlsContent,
  cancelHandler,
  saveHandler,
  tabs: _Tabs, // optional
  tabsValidationShared = true, // validate all tabs on save or only selected tab
  saveErrors = {},
  children,
  isSaving = false,
  outerChanges,
  size = "450px",
}) => {
  const classes = useStyles();
  const [data, setData] = useState(InitialObject);
  const [changes, setChanges] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [selectedTabId, setSelectedTabId] = useState<any>(
    _Tabs && _Tabs.length > 0 && _Tabs[0].name
  );

  useEffect(() => {
    if (outerChanges) {
      setChanges(true);
    }
  }, [outerChanges]);

  useEffect(() => {
    if (Object.keys(saveErrors).length > 0) {
      setErrors((errs) => {
        return {
          ...errs,
          ...saveErrors,
        };
      });
    }
  }, [saveErrors]);

  const onChange = (control: IControl, newVal: any, errMessage: string, newText: any): void => {
    setErrors({});

    setData((prevData: any) => {
      const newData = {
        ...prevData,
        [control.field]: newVal,
      };
      return newData;
    });

    setChanges(true); // todo: compare to initial state

    if (control.onChange) {
      control.onChange(control, newVal, errMessage, newText);
    }
  };

  const onTabClick = (tab: string | number) => {
    setSelectedTabId(tab);
  };

  const save = () => {
    let validations = {};

    if (_Tabs) {
      _Tabs.forEach((tab: any) => {
        if (tabsValidationShared || tab.name === selectedTabId) {
          validations = {
            ...validations,
            ...Validation.validateControls(tab.controls, data),
          };
        }
      });
    } else {
      //@ts-ignore
      validations = Validation.validateControls(controls, data);
    }

    const newErrors = {
      ...errors,
      ...validations,
    };

    setErrors(newErrors);

    if (saveHandler && Object.values(validations).filter((err) => !!err).length === 0) {
      //@ts-ignore
      saveHandler(data, selectedTabId);
    }
  };

  const cancel = () => {
    setData(InitialObject);
    if (cancelHandler) {
      cancelHandler();
    }
  };

  const getFormControls = (controls: IControl[]) => {
    const formControls = controls.map((control, index) => {
      return (
        <FDFormControl
          control={{ ...control, readonly: readonly || control.readonly }}
          errorMessage={`${errors[control.field] || ""}`}
          key={`flyout_form_ctrl_${index}`}
          onChange={onChange}
          source={data}
        />
      );
    });

    return formControls;
  };

  const getProfileFormControls = (controls: IControl[]) => {
    return (
      <>
        <EuiText size="s" textAlign="left" style={{ fontWeight: 600 }}>
          Personal Information
        </EuiText>
        <EuiFlexGroup direction="row">
          <EuiFlexItem grow={1}>
            <FDFormControl
              control={{ ...controls[0], readonly: readonly || controls[0].readonly }}
              errorMessage={`${errors[controls[0].field] || ""}`}
              key={`flyout_form_ctrl_${0}`}
              onChange={onChange}
              source={data}
            />
          </EuiFlexItem>
          <EuiFlexItem grow={1}>
            <FDFormControl
              control={{ ...controls[1], readonly: readonly || controls[1].readonly }}
              errorMessage={`${errors[controls[1].field] || ""}`}
              key={`flyout_form_ctrl_${1}`}
              onChange={onChange}
              source={data}
            />
          </EuiFlexItem>
        </EuiFlexGroup>

        <EuiFlexGroup direction="row">
          <EuiFlexItem grow={1}>
            <FDFormControl
              control={{ ...controls[2], readonly: readonly || controls[2].readonly }}
              errorMessage={`${errors[controls[2].field] || ""}`}
              key={`flyout_form_ctrl_${2}`}
              onChange={onChange}
              source={data}
            />
          </EuiFlexItem>
          <EuiFlexItem grow={1}>
            <FDFormControl
              control={{ ...controls[3], readonly: readonly || controls[3].readonly }}
              errorMessage={`${errors[controls[3].field] || ""}`}
              key={`flyout_form_ctrl_${3}`}
              onChange={onChange}
              source={data}
            />
          </EuiFlexItem>
        </EuiFlexGroup>

        <EuiFlexGroup direction="row">
          <EuiFlexItem grow={1}>
            <FDFormControl
              control={{ ...controls[4], readonly: readonly || controls[4].readonly }}
              errorMessage={`${errors[controls[4].field] || ""}`}
              key={`flyout_form_ctrl_${4}`}
              onChange={onChange}
              source={data}
            />
          </EuiFlexItem>
          <EuiFlexItem grow={1}>
            <FDFormControl
              control={{ ...controls[5], readonly: readonly || controls[5].readonly }}
              errorMessage={`${errors[controls[5].field] || ""}`}
              key={`flyout_form_ctrl_${5}`}
              onChange={onChange}
              source={data}
            />
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiFlexGroup direction="row">
          <EuiFlexItem grow={1}>
            <FDFormControl
              control={{ ...controls[6], readonly: readonly || controls[6].readonly }}
              errorMessage={`${errors[controls[6].field] || ""}`}
              key={`flyout_form_ctrl_${6}`}
              onChange={onChange}
              source={data}
            />
          </EuiFlexItem>
        </EuiFlexGroup>

        <EuiText size="s" textAlign="left" style={{ fontWeight: 600 }}>
          Contact Information
        </EuiText>
        <EuiFlexGroup direction="row">
          <EuiFlexItem grow={1}>
            <FDFormControl
              control={{ ...controls[7], readonly: readonly || controls[7].readonly }}
              errorMessage={`${errors[controls[7].field] || ""}`}
              key={`flyout_form_ctrl_${7}`}
              onChange={onChange}
              source={data}
            />
          </EuiFlexItem>
          <EuiFlexItem grow={1}>
            <FDFormControl
              control={{ ...controls[8], readonly: readonly || controls[8].readonly }}
              errorMessage={`${errors[controls[8].field] || ""}`}
              key={`flyout_form_ctrl_${8}`}
              onChange={onChange}
              source={data}
            />
          </EuiFlexItem>
        </EuiFlexGroup>
      </>
    );
  };

  const getReadonlyDescriptionList = () => {
    const listItems = readonlyList.map((item) => {
      return {
        title: item.label,
        description: InitialObject[item.field],
      };
    });

    return <EuiDescriptionList compressed listItems={listItems} type="row" />;
  };

  const tabs = _Tabs?.map((tab: any) => ({
    id: tab.name,
    name: tab.name,
    content: (
      <>
        {tab.readonly && Array.isArray(tab.list) && (
          <>
            <EuiSpacer />
            {tab.list.length > 0 && (
              <EuiDescriptionList compressed listItems={tab.list} type="row" />
            )}

            {tab.list.length === 0 && (
              <>
                <EuiText size="s" textAlign="center">
                  No Items Found.
                </EuiText>
              </>
            )}
          </>
        )}

        {!tab.readonly && (
          <EuiFlexGroup direction="column">
            <>
              {_Tabs && _Tabs?.length > 1 && <EuiSpacer size={tab?.isProfile ? "xs" : "l"} />}
              {!tab?.isProfile && getFormControls(tab.controls)}
              {tab?.isProfile && getProfileFormControls(tab.controls)}
            </>
          </EuiFlexGroup>
        )}
      </>
    ),
  }));

  let flyoutBody: ReactNode | null = null;
  if (tabs) {
    flyoutBody = (
      <>
        {tabs.length > 1 && (
          <Tabs items={tabs} selectedTabId={selectedTabId} onClick={onTabClick} />
        )}

        {tabs?.find((i) => i.name === selectedTabId)?.content}
      </>
    );
  } else if (controls.length > 0) {
    //@ts-ignore
    const formControls = getFormControls(controls);
    flyoutBody = (
      <EuiForm>
        <EuiFlexGroup direction="column">
          <>{formControls}</>
        </EuiFlexGroup>
      </EuiForm>
    );
  } else if (InitialObject && readonlyList.length > 0) {
    flyoutBody = getReadonlyDescriptionList();
  }

  return (
    <>
      <Flyout
        title={title}
        size={size}
        onClose={cancel}
        children={
          <>
            {topControlsContent}
            {flyoutBody}
            {children}
          </>
        }
        footerChildren={
          <>
            <ErrorsList errors={errors} />

            {readonlyList.length === 0 && (
              <FdFormButtons
                isRightButtonDisabled={!changes || readonly}
                onMiddleButtonClick={cancel}
                onRightButtonClick={save}
                isRightButtonLoading={isSaving}
              />
            )}

            {readonlyList.length > 0 && (
              <>
                <EuiFlexGroup justifyContent="spaceBetween">
                  <EuiFlexItem grow={false}>
                    <Button type="secondary" text="Close" onClick={cancel} />
                  </EuiFlexItem>
                </EuiFlexGroup>
              </>
            )}
          </>
        }
      />
    </>
  );
};

export default FlyoutForm;
